import { useEffect, createContext, useContext, useRef } from 'react';

import { useLocation } from 'react-router-dom';
import { trackPageView } from '../lib/analytics';

export const SITE_URL = 'https://pointbreak.systems';
export const SITE_NAME = 'Point Break Systems';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

export interface SeoOptions {
  /** Full page title, including the site name suffix if desired. */
  title: string;
  description: string;
  /** Path starting with "/", e.g. "/faq". Used to build canonical + og:url. */
  path: string;
  /** Absolute image URL. Falls back to the default social share image. */
  image?: string;
  type?: 'website' | 'article';
  /** Any number of JSON-LD objects to inject for this page. */
  schemas?: Record<string, unknown>[];
  noindex?: boolean;
  /** Set to false to skip all work, e.g. while async page data is still loading. */
  enabled?: boolean;
}

export const SeoCollectorContext = createContext<((options: SeoOptions) => void) | null>(null);

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  const matches = [...document.querySelectorAll<HTMLMetaElement>(`meta[name="${key}"], meta[property="${key}"]`)];
  let el = matches.shift();
  matches.forEach(node => node.remove());
  if (el) { el.removeAttribute(attr === 'name' ? 'property' : 'name'); el.setAttribute(attr, key); }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Shared per-page SEO hook: sets title, description, canonical, Open Graph,
 * and Twitter Card tags, and injects any JSON-LD schemas passed in. Call once
 * per page component. JSON-LD scripts are cleaned up on unmount; meta tags
 * and the canonical link are overwritten in place by whichever page mounts
 * next, so no flash of stale content occurs on route change.
 */
export function useSeo({ title, description, path, image, type = 'website', schemas = [], noindex = false, enabled = true }: SeoOptions) {
  const location = useLocation();
  const collect = useContext(SeoCollectorContext);
  const trackedUrl = useRef('');
  if (enabled) collect?.({ title, description, path, image, type, schemas, noindex });
  useEffect(() => {
    if (!enabled) return;

    const url = `${SITE_URL}${path}`;
    const ogImage = image || DEFAULT_OG_IMAGE;

    document.title = title;
    document.querySelectorAll('script[data-prerender-seo]').forEach(el => el.remove());

    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:site_name', SITE_NAME);

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:url', url);
    setMeta('name', 'twitter:image', ogImage);
    document.querySelectorAll('meta[property="article:published_time"], meta[property="article:modified_time"], meta[property="article:author"]').forEach(el => el.remove());
    const article = schemas.find(schema => schema['@type'] === 'BlogPosting');
    if (article) {
      if (article.datePublished) setMeta('property', 'article:published_time', String(article.datePublished));
      if (article.dateModified) setMeta('property', 'article:modified_time', String(article.dateModified));
    }

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    const scriptEls = schemas.map((schema) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
      return s;
    });

    const pageUrl = window.location.pathname + window.location.search;
    if (trackedUrl.current !== pageUrl) {
      trackedUrl.current = pageUrl;
      trackPageView(pageUrl, title);
    }

    return () => {
      scriptEls.forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, title, description, path, image, type, noindex, location.pathname, location.search, JSON.stringify(schemas)]);
}
